/**
 * How a shortcut is written down.
 *
 * The handlers have always accepted both modifiers — `event.metaKey ||
 * event.ctrlKey`, in `app-shell.tsx` for ⌘Z/⇧⌘Z and ⌘J and in
 * `command-palette.tsx` for ⌘K — so Ctrl+K has worked on Windows and Linux
 * since the day the palette landed. Only the *labels* were Mac-only, which is
 * the worst of both: the key works, and the screen says it does not.
 *
 * Detection happens once, at module load, and every label reads the same
 * constant. A per-render check would be a lie about what can change: the
 * platform cannot, and two labels on one screen disagreeing about which
 * modifier this keyboard has would be worse than either answer alone.
 *
 * The formatters take `apple` explicitly so both branches are testable in one
 * jsdom, where `navigator` reports whatever the runner happens to be. The
 * convenience wrappers below bind the detected platform and are what components
 * call.
 *
 * Only *modified* keys need this. Plain `A` — the add-ingredient shortcut — is
 * the same character on every keyboard, so it is written as itself and has no
 * formatter here; adding one would suggest there is a platform question where
 * there is none.
 */

/** Apple keyboards get the glyphs; everything else gets the words. */
export function formatCmd(key: string, apple: boolean): string {
  return apple ? `⌘${key}` : `Ctrl+${key}`;
}

/**
 * Shift plus command.
 *
 * The order follows each platform's own convention: macOS writes modifiers in
 * the fixed glyph order ⌃⌥⇧⌘, so Shift comes *before* Command, while Windows
 * and Linux spell them out in the order they are held, Ctrl then Shift.
 */
export function formatShiftCmd(key: string, apple: boolean): string {
  return apple ? `⇧⌘${key}` : `Ctrl+Shift+${key}`;
}

/**
 * Whether this browser is on an Apple keyboard.
 *
 * `userAgentData.platform` is the modern answer and the only one that is not
 * deprecated; `navigator.platform` is the fallback every non-Chromium browser
 * still needs. Both are read inside a guard: this module is imported by
 * components that also render under jsdom and in the SSR-less build's node
 * pass, where `navigator` may be absent or a stub, and a legend is never worth
 * throwing over. When nothing can be determined the answer is `false` — Ctrl is
 * the spelling that names a real key on a Mac too (the handlers accept it),
 * whereas ⌘ on a PC names nothing.
 */
function detectApplePlatform(): boolean {
  try {
    if (typeof navigator === 'undefined') return false;
    const agent = navigator as Navigator & {
      readonly userAgentData?: { readonly platform?: string };
    };
    const platform = agent.userAgentData?.platform ?? navigator.platform ?? '';
    return /mac|iphone|ipad|ipod/i.test(platform);
  } catch {
    return false;
  }
}

/** Resolved once — see the note above about two labels disagreeing. */
export const IS_APPLE_PLATFORM: boolean = detectApplePlatform();

/** `cmd('K')` → `⌘K` on a Mac, `Ctrl+K` everywhere else. */
export function cmd(key: string): string {
  return formatCmd(key, IS_APPLE_PLATFORM);
}

/** `shiftCmd('Z')` → `⇧⌘Z` on a Mac, `Ctrl+Shift+Z` everywhere else. */
export function shiftCmd(key: string): string {
  return formatShiftCmd(key, IS_APPLE_PLATFORM);
}
